/**
 * ResourceVideoUpload
 * Single MP4 upload control for a Video-type resource, mirroring the
 * CoverImageUpload UX (drag-drop / click, replace, remove, retry). The
 * uploaded file is stored as a VIDEO ResourceFile and plays inline on the
 * resource detail page like a linked video.
 */

import { useState, useRef, useCallback } from 'react';
import { X, RefreshCw, Film } from 'lucide-react';
import { apiClient } from '@/core/api/client';
import type { MediaFile } from './CoverImageUpload';

type ResourceVideoUploadProps = {
  videoFile: MediaFile | null;
  onVideoFileChange: (file: MediaFile | null) => void;
};

const ACCEPTED_VIDEO_TYPES = 'video/mp4,video/webm,video/quicktime';
// Feature 093: resource-hub MP4s get a raised 205MB cap (backend
// /files/upload/resource-video); other video types keep the standard 100MB.
const MAX_MP4_BYTES = 205 * 1024 * 1024;
const MAX_OTHER_VIDEO_BYTES = 100 * 1024 * 1024;

const maxBytesFor = (mimeType: string): number =>
  mimeType === 'video/mp4' ? MAX_MP4_BYTES : MAX_OTHER_VIDEO_BYTES;

const maxLabelFor = (mimeType: string): string =>
  mimeType === 'video/mp4' ? '205 MB' : '100 MB';

export function ResourceVideoUpload({ videoFile, onVideoFileChange }: ResourceVideoUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [failedFile, setFailedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = useCallback(
    async (file: File) => {
      const allowedTypes = ACCEPTED_VIDEO_TYPES.split(',');
      if (!allowedTypes.includes(file.type)) {
        setFailedFile(null);
        setUploadError('Only MP4, WebM, or MOV video files are accepted.');
        return;
      }
      if (file.size > maxBytesFor(file.type)) {
        setFailedFile(null);
        setUploadError(`Video is too large. The maximum size is ${maxLabelFor(file.type)}.`);
        return;
      }
      setIsUploading(true);
      setUploadError(null);
      setFailedFile(null);

      const formData = new FormData();
      formData.append('file', file);

      try {
        const { data } = await apiClient.post('/files/upload/resource-video', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        onVideoFileChange({
          fileName: file.name,
          fileUrl: data.publicUrl,
          mimeType: file.type,
          fileSize: Math.round(file.size / 1024),
        });
      } catch {
        setUploadError('Upload failed. Please try again.');
        setFailedFile(file);
      } finally {
        setIsUploading(false);
      }
    },
    [onVideoFileChange],
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await uploadFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) await uploadFile(file);
    },
    [uploadFile],
  );

  const handleRemove = () => {
    onVideoFileChange(null);
    setUploadError(null);
    setFailedFile(null);
  };

  const handleRetry = () => {
    if (failedFile) uploadFile(failedFile);
  };

  return (
    <div data-testid="resource-video-upload" className="space-y-3">
      <h3 className="text-sm font-medium text-gray-700">Video</h3>

      {videoFile ? (
        <div className="relative rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
          <video
            data-testid="video-upload-preview"
            src={videoFile.fileUrl}
            controls
            preload="metadata"
            className="w-full max-h-64 bg-black"
          />
          <div className="absolute top-2 right-2 flex gap-1">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-1.5 bg-white/90 rounded-md shadow hover:bg-white transition-colors"
              aria-label="Replace video"
            >
              <RefreshCw className="h-4 w-4 text-gray-600" />
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="p-1.5 bg-white/90 rounded-md shadow hover:bg-white transition-colors"
              aria-label="Remove video"
            >
              <X className="h-4 w-4 text-gray-600" />
            </button>
          </div>
          <div className="px-3 py-2 text-xs text-gray-500 truncate">
            {videoFile.fileName}
            {videoFile.fileSize && <span className="ml-2">{videoFile.fileSize} KB</span>}
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragOver
              ? 'border-[#026857] bg-[#026857]/5'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          {isUploading ? (
            <RefreshCw className="h-8 w-8 mx-auto mb-2 text-gray-400 animate-spin" />
          ) : (
            <Film className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          )}
          <p className="text-sm text-gray-600">
            {isUploading ? 'Uploading...' : 'Drag & drop or click to upload a video'}
          </p>
          <p className="text-xs text-gray-400 mt-1">MP4 up to 205 MB; WebM or MOV up to 100 MB</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        data-testid="resource-video-input"
        type="file"
        accept={ACCEPTED_VIDEO_TYPES}
        onChange={handleFileSelect}
        className="hidden"
      />

      {uploadError && (
        <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          <span>{uploadError}</span>
          {failedFile && (
            <button
              type="button"
              onClick={handleRetry}
              className="flex items-center gap-1 ml-auto text-red-600 hover:text-red-800"
              aria-label="Retry"
            >
              <RefreshCw className="h-3 w-3" />
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}
